import React, { memo, useCallback, useEffect, useRef } from "react";
import _noop from "lodash/noop";
import styled from "@emotion/styled";
import JsBarcode from "react-barcode";
import { QRCodeSVG } from "qrcode.react";

import { DELIVERY_TYPE_NORMAL } from "@/Models/Delivery";

const PrintContainer = styled.div``;
const WaybillContainer = styled.div``;

/**
 * Style
 */
const DOOBALHERO_LOGO_SRC = `${window.location.origin}${process.env
  .PUBLIC_URL || ""}/assets/images/doobalhero_logo.png`;

const PrintContainerStyle = {
  padding: 0,
  margin: 0,
};

const WaybillContainerStyle = {
  position: "relative",
  width: "100mm",
  height: "6in",
  margin: 0,
  padding: 0,
  overflow: "hidden",
  clipPath: "inset(0)",
  boxSizing: "border-box",
  pageBreakAfter: "always",
  breakAfter: "page",
  pageBreakInside: "avoid",
  breakInside: "avoid",
};

const DefaultStyle = {
  transform: "rotate(-90deg)",
  transformOrigin: "left top",
  position: "absolute",
  fontSize: `11px`,
  whiteSpace: "pre-wrap",
  lineHeight: "3mm",
};

const Barcode = ({ bookId, onLoaded = _noop }) => {
  useEffect(() => {
    onLoaded();
  });

  return (
    <DefaultComponent
      style={{
        top: "148mm",
        left: "79mm",
        width: "60mm",
        height: "12mm",
      }}
    >
      <JsBarcode
        margin={0}
        width={1.7}
        height={40}
        value={bookId}
        textPosition="top"
      />
    </DefaultComponent>
  );
};

const BarcodeForSpecificCorpUser = ({ code, title = "" }) => {
  return (
    <DefaultComponent
      style={{
        top: "53mm",
        left: "25mm",
        fontSize: "10px",
        whiteSpace: "pre-wrap",
        lineHeight: "3mm",
      }}
    >
      <div
        style={{
          top: "35mm",
          left: "22mm",
          fontSize: "10px",
          whiteSpace: "pre-wrap",
          lineHeight: "3mm",
          textAlign: "center",
        }}
      >
        <p
          style={{
            margin: 0,
            padding: 0,
          }}
        >
          {title}
        </p>
        <JsBarcode
          fontSize="10"
          margin={0}
          width={1.3}
          height={37}
          value={code}
        />
      </div>
    </DefaultComponent>
  );
};

const BookIdQRCode = ({ bookId }) => {
  return (
    <DefaultComponent
      style={{
        top: "82mm",
        left: "83mm",
        width: "15mm",
        height: "15mm",
      }}
    >
      <QRCodeSVG
        value={bookId}
        style={{ width: "15mm", height: "15mm", display: "block" }}
      />
    </DefaultComponent>
  );
};

const DefaultComponent = ({ children, style = {} }) => {
  return <div style={{ ...DefaultStyle, ...style }}>{children}</div>;
};

const OverlayFrame = memo(({ onAfterRendered = _noop }) => {
  const doneRef = useRef(false);

  const handleDone = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    onAfterRendered();
  }, [onAfterRendered]);

  useEffect(() => {
    const timer = setTimeout(handleDone, 500);
    return () => clearTimeout(timer);
  }, [handleDone]);

  return (
    <svg
      width="100mm"
      height="6in"
      viewBox="0 0 100 152.4"
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        display: "block",
      }}
    >
      <g transform="translate(0 152.4) rotate(-90)">
        <rect
          x="0.25"
          y="0.25"
          width="151.9"
          height="99.5"
          fill="none"
          stroke="#000"
          strokeWidth="0.5"
        />
        <line
          x1="0"
          y1="12"
          x2="152.4"
          y2="12"
          stroke="#000"
          strokeWidth="0.5"
        />
        <line x1="65" y1="12" x2="65" y2="76" stroke="#000" strokeWidth="0.5" />
        <line x1="8" y1="12" x2="8" y2="76" stroke="#000" strokeWidth="0.5" />
        <line
          x1="0"
          y1="26"
          x2="152.4"
          y2="26"
          stroke="#000"
          strokeWidth="0.5"
        />
        <line x1="0" y1="76" x2="65" y2="76" stroke="#000" strokeWidth="0.5" />
        <image
          href={DOOBALHERO_LOGO_SRC}
          x="3"
          y="3"
          height="7.6"
          preserveAspectRatio="xMinYMid meet"
          onLoad={handleDone}
          onError={handleDone}
        />
        <text
          x="149"
          y="9.2"
          textAnchor="end"
          fontSize="5.5"
          fontWeight="700"
          fontStyle="italic"
          letterSpacing="0.4"
        >
          오늘 사면 오늘 도착! 전국 당일배송!
        </text>
        <text x="4" y="15.5" textAnchor="middle" fontSize="3.2" fontWeight="700">
          <tspan x="4" dy="0">
            보
          </tspan>
          <tspan x="4" dy="4">
            낸
          </tspan>
          <tspan x="4" dy="4">
            분
          </tspan>
        </text>
        <text x="4" y="44" textAnchor="middle" fontSize="3.2" fontWeight="700">
          <tspan x="4" dy="0">
            받
          </tspan>
          <tspan x="4" dy="4">
            는
          </tspan>
          <tspan x="4" dy="4">
            분
          </tspan>
        </text>
        <text x="149.5" y="94" textAnchor="end" fontSize="2.6" fontWeight="700">
          계약문의 : doobalhero.kr
        </text>
        <text x="149.5" y="97.5" textAnchor="end" fontSize="2.2" fontWeight="400">
          ※개인정보 보호를 위하여 수령하신 운송장을 폐기하여 주시기 바랍니다.
        </text>
      </g>
    </svg>
  );
});

const DongCode = ({ dongCode }) => {
  return (
    <DefaultComponent
      style={{
        top: `${5 +
          8 * dongCode.returnPickup.length +
          4 * dongCode.sidodong.length +
          8 * dongCode.allocationGroup.length}mm`,
        left: "17mm",
        textAlign: "right",
        letterSpacing: "0.1mm",
      }}
    >
      <span
        style={{
          fontSize: dongCode.returnPickup ? "40px" : "20px",
          fontWeight: "bold",
          whiteSpace: "pre",
        }}
      >
        {dongCode.returnPickup || dongCode.sidodong}
      </span>
      <span
        style={{
          fontSize: "40px",
          fontWeight: "bold",
          whiteSpace: "pre",
        }}
      >
        {dongCode.allocationGroup}
      </span>
    </DefaultComponent>
  );
};

const Etc = memo(({ orderIdFromCorp, logoImage, onAfterRendered = _noop }) => {
  useEffect(() => {
    if (!logoImage) {
      setTimeout(() => {
        onAfterRendered();
      }, 200);
    }
  }, []);

  const etcStyle = {
    top: "87mm",
    left: "5mm",
    fontSize: "12px",
    whiteSpace: "pre",
  };

  if (orderIdFromCorp) {
    return (
      <DefaultComponent style={etcStyle}>
        {orderIdFromCorp.length > 20
          ? `${orderIdFromCorp.slice(0, 20)}\n${orderIdFromCorp.slice(20)}`
          : orderIdFromCorp}
      </DefaultComponent>
    );
  } else if (logoImage) {
    return (
      <DefaultComponent style={etcStyle}>
        <img
          onLoad={onAfterRendered}
          alt="기업 로고 이미지"
          src={logoImage.src}
          style={{
            width: logoImage.width,
            height: logoImage.height,
          }}
        />
      </DefaultComponent>
    );
  } else {
    return null;
  }
});

const Memo = ({ children }) => {
  return (
    <DefaultComponent
      style={{
        top: "140mm",
        left: "66mm",
        width: "55mm",
        fontSize: "14px",
        fontWeight: "bold",
        lineHeight: "3.5mm",
      }}
    >
      {children}
    </DefaultComponent>
  );
};

const OrderIdFromCorpWithSpotInfo = ({ orderIdFromCorp, spot }) => {
  return (
    <DefaultComponent
      style={{
        top: "82mm",
        left: "28mm",
        fontSize: "10px",
        whiteSpace: "pre-wrap",
        lineHeight: "3mm",
        width: "81mm",
      }}
    >
      {orderIdFromCorp && <span>주문번호 {orderIdFromCorp} </span>}
      {spot && (
        <span>
          ({spot.name}/T.{spot.contactForLabel || ""})
        </span>
      )}
    </DefaultComponent>
  );
};

const ProductNames = ({ productNames = [], delivery = {} }) => {
  const customProductName =
    delivery.isSpotCovering ||
    delivery.isSpotUgleelab ||
    delivery.isSpotDduckhamji;

  return (
    <DefaultComponent
      style={{
        top: "85mm",
        left: "41mm",
        lineHeight:
          delivery.isSpotKukka || delivery.isSpotDialCommunication
            ? "4.2mm"
            : "3.5mm",
        fontSize:
          delivery.isSpotKukka || delivery.isSpotDialCommunication
            ? "17px"
            : "12px",
        fontWeight: "bold",
        width: "80mm",
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
      }}
    >
      <div>
        {!customProductName &&
          productNames?.map((name, index) => {
            return (
              <div key={`${name}-${index}`}>
                {Array.isArray(name) ? name.join("") : name}{" "}
              </div>
            );
          })}
        {delivery.isSpotCovering &&
          productNames?.map((name, index) => {
            const fullName = Array.isArray(name) ? name.join("") : name;
            const [firstWord, ...rest] = fullName.split(" ");

            return (
              <div
                key={`${name}-${index}`}
                style={{
                  fontSize: "30px",
                  lineHeight: "8mm",
                  marginTop: "4px",
                }}
              >
                *{firstWord.slice(1)}* {rest.join(" ")}{" "}
              </div>
            );
          })}
        {delivery.isSpotUgleelab &&
          productNames?.map((name, index) => {
            const fullName = Array.isArray(name) ? name.join("") : name;
            const [firstWord, ...rest] = fullName.split(" ");
            const displayedFirstWord = firstWord.slice(1);
            const isTodayPickup = displayedFirstWord === "오늘수거";

            return (
              <div
                key={`${name}-${index}`}
                style={{
                  fontSize: "30px",
                  lineHeight: "8mm",
                  marginTop: "4px",
                }}
              >
                {displayedFirstWord}
                {isTodayPickup ? <br /> : " "}
                {rest.join(" ")}
              </div>
            );
          })}
        {delivery.isSpotDduckhamji && (
          <div
            style={{
              fontSize: "20px",
              lineHeight: "6mm",
              marginTop: "4px",
            }}
          >
            {productNames?.map((name, index) => {
              return (
                <div key={`${name}-${index}`}>
                  {Array.isArray(name) ? name.join("") : name}{" "}
                </div>
              );
            })}
            <div style={{ fontSize: "18px" }}>
              떡입니다빠른배송
              <br />
              보내는분:{delivery.displaySenderName}
              {delivery.displaySenderMobile}
            </div>
          </div>
        )}
      </div>
    </DefaultComponent>
  );
};

const ProductNamesRest = ({ productNames = [] }) => {
  return (
    <DefaultComponent
      style={{
        top: "85mm",
        left: "15mm",
        lineHeight: "3.5mm",
        fontSize: "12px",
        fontWeight: "bold",
        whiteSpace: "pre-wrap",
      }}
    >
      {productNames.join("\n")}
    </DefaultComponent>
  );
};

const ProductTotalCount = ({ totalCount, numbering }) => {
  return (
    <DefaultComponent
      style={{
        top: "82mm",
        left: "34mm",
        fontSize: "10px",
        whiteSpace: "pre-wrap",
      }}
    >
      <div>{totalCount}</div>
      <div>{numbering}</div>
    </DefaultComponent>
  );
};

const ReceiptDate = ({ children }) => {
  return (
    <DefaultComponent
      style={{
        top: "82mm",
        left: "31mm",
        fontSize: "10px",
        whiteSpace: "pre-wrap",
        lineHeight: "3mm",
      }}
    >
      {children}
    </DefaultComponent>
  );
};

const ReceiverAddress = ({
  receiverAddressWithoutSigungu,
  receiverAddressRoadWithoutSigungu,
  receiverBuilding,
}) => {
  const _text = `${`${receiverAddressRoadWithoutSigungu} ${receiverBuilding}`.trim()}/${receiverAddressWithoutSigungu}`;

  const text1 = _text.slice(0, 15);
  const text2 = _text.slice(15, 30);
  const text3 = _text.slice(30);

  return (
    <>
      <DefaultComponent
        style={{
          top: "140mm",
          left: "28mm",
          fontSize: "17px",
          fontWeight: "bold",
          lineHeight: "4.1mm",
        }}
      >
        {text1}
      </DefaultComponent>
      <DefaultComponent
        style={{
          top: "140mm",
          left: "33mm",
          fontSize: "17px",
          fontWeight: "bold",
          lineHeight: "4.1mm",
        }}
      >
        {text2}
      </DefaultComponent>
      <DefaultComponent
        style={{
          top: "140mm",
          left: "38mm",
          fontSize: "17px",
          fontWeight: "bold",
          lineHeight: "4.1mm",
        }}
      >
        {text3}
      </DefaultComponent>
    </>
  );
};

const ReceiverNameAndMobile = ({ receiverName, receiverMobile }) => {
  return (
    <DefaultComponent
      style={{
        top: "140mm",
        left: "58mm",
        width: "400px",
      }}
    >
      <div
        style={{
          fontSize: "16px",
          fontWeight: "bold",
          lineHeight: "3mm",
        }}
      >
        {`<${receiverName}>`}
      </div>
      <div
        style={{
          fontSize: "16px",
          marginTop: "4px",
          fontWeight: "bold",
          lineHeight: "3mm",
        }}
      >
        T: {`${receiverMobile}`.trim()}
      </div>
    </DefaultComponent>
  );
};

const RestProductComponets = ({
  numOfLinesRestProductNames,
  restProductNames,
}) => {
  let productNamesComponents = [];
  for (
    let i = 0;
    i < restProductNames.length;
    i += numOfLinesRestProductNames
  ) {
    productNamesComponents.push(
      <WaybillContainer key={i} className="label" style={WaybillContainerStyle}>
        <ProductNamesRest
          productNames={restProductNames.slice(
            i,
            i + numOfLinesRestProductNames,
          )}
        />
        <PrintBreak />
      </WaybillContainer>,
    );
  }

  return productNamesComponents;
};

const SenderName = ({ children }) => {
  return (
    <DefaultComponent
      style={{
        top: "140mm",
        left: "15mm",
        fontSize: "16px",
        fontWeight: "bold",
        width: "100mm",
      }}
    >
      {children}
    </DefaultComponent>
  );
};

const SenderAddress = ({ children }) => {
  return (
    <DefaultComponent
      style={{
        top: "140mm",
        left: "19mm",
        fontSize: "11px",
        width: "65mm",
      }}
    >
      {children}
    </DefaultComponent>
  );
};

const PrintBreak = () => {
  return <div sytle={{ pageBreakAfter: "alwayws" }}></div>;
};

const getCharWidth = (char) => {
  if (/[가-힣]/.test(char)) return 1; // 한글
  if (/[A-Z]/.test(char)) return 0.6; // 영문 대문자
  if (/[a-z]/.test(char)) return 0.5; // 영문 소문자
  if (/[0-9]/.test(char)) return 0.5; // 숫자
  if (/\s/.test(char)) return 0.5; // 공백
  if (/[.,\-/]/.test(char)) return 0.3; // 특수문자 (주소에 자주 쓰이는)
  return 0.5; // 그 외
};

const splitByVisualWidth = (text, maxWidth = 15) => {
  const lines = [];
  let currentLine = "";
  let currentWidth = 0;

  for (const char of text) {
    const charWidth = getCharWidth(char);

    if (currentWidth + charWidth > maxWidth) {
      lines.push(currentLine);
      currentLine = char;
      currentWidth = charWidth;
    } else {
      currentLine += char;
      currentWidth += charWidth;
    }
  }

  if (currentLine) lines.push(currentLine);

  return [lines[0] || "", lines[1] || "", lines[2] || ""];
};

const UnrefinedAddress = ({ unrefinedAddress }) => {
  const _text = unrefinedAddress ? unrefinedAddress.trim() : "";
  const [text1, text2, text3] = splitByVisualWidth(_text, 15);

  return (
    <>
      <DefaultComponent
        style={{
          top: "140mm",
          left: "43mm",
          width: "55mm",
          fontSize: "14px",
          fontWeight: "bold",
          lineHeight: "4mm",
        }}
      >
        {text1}
      </DefaultComponent>
      <DefaultComponent
        style={{
          top: "140mm",
          left: "46.5mm",
          width: "55mm",
          fontSize: "14px",
          fontWeight: "bold",
          lineHeight: "4mm",
        }}
      >
        {text2}
      </DefaultComponent>
      <DefaultComponent
        style={{
          top: "140mm",
          left: "50mm",
          width: "55mm",
          fontSize: "14px",
          fontWeight: "bold",
          lineHeight: "4mm",
        }}
      >
        {text3}
      </DefaultComponent>
    </>
  );
};

const WaybillWrap = memo(
  ({ delivery, count, totalCount, handleAfterRendered = _noop }) => {
    return (
      <>
        <WaybillContainer className="label" style={WaybillContainerStyle}>
          <OverlayFrame onAfterRendered={handleAfterRendered} />

          {/** 운송장 상단 영역 */}
          <Etc
            logoImage={delivery.displayCorpLogoImageObjBySpotCode}
            orderIdFromCorp={delivery.displayOrderIdFromCorpBySpotCode}
          />

          {/** 운송장 왼쪽 영역 */}
          {delivery.type === DELIVERY_TYPE_NORMAL ? (
            <>
              <SenderName>
                {delivery.displaySenderName} T. {delivery.displaySenderMobile}
              </SenderName>
              <SenderAddress>
                {delivery.displaySenderAddress}
                <br />
                {delivery.displaySenderAddressDetail}
              </SenderAddress>
              <ReceiverAddress
                receiverAddressWithoutSigungu={
                  delivery.displayReceiverAddressWithoutSigungu
                }
                receiverAddressRoadWithoutSigungu={
                  delivery.displayReceiverAddressRoadWithoutSigungu
                }
                receiverBuilding={delivery.displayReceiverBuilding || ""}
              />
              <UnrefinedAddress
                unrefinedAddress={delivery.displayUnrefinedAddress}
              />
              <ReceiverNameAndMobile
                receiverName={delivery.displayReceiverName}
                receiverMobile={delivery.displayReceiverMobile}
              />
            </>
          ) : (
            <>
              <SenderName>
                {delivery.displayReceiverName} T.{" "}
                {delivery.displayReceiverMobile}
              </SenderName>
              <SenderAddress>
                {delivery.receiverAddress}
                <br />
                {delivery.displayReceiverAddressDetail}
              </SenderAddress>
              <ReceiverAddress
                receiverAddressWithoutSigungu={
                  delivery.senderAddressWithoutSigungu
                }
                receiverAddressRoadWithoutSigungu={
                  delivery.senderAddressRoadWithoutSigungu
                }
                receiverBuilding={delivery.senderBuilding || ""}
              />
              <UnrefinedAddress
                unrefinedAddress={delivery.displayUnrefinedAddress}
              />
              <ReceiverNameAndMobile
                receiverName={delivery.displaySenderName}
                receiverMobile={delivery.displaySenderMobile}
              />
            </>
          )}

          <Memo>{delivery.memoFromCustomer}</Memo>
          <Barcode bookId={delivery.bookId} />

          {/** 운송장 오른쪽 영역 */}
          <DongCode dongCode={delivery.dongCode} />
          <OrderIdFromCorpWithSpotInfo
            orderIdFromCorp={delivery.orderIdFromCorp}
            spot={delivery.spot}
          />

          <ProductTotalCount
            totalCount={delivery.renderTotalProductCount()}
            numbering={delivery.renderNumbering(count, totalCount)}
          />

          <ReceiptDate>
            {delivery.renderReceiptDate("yyyy/MM/dd HH:mm")}
          </ReceiptDate>

          <ProductNames
            productNames={delivery.firstProductNames}
            delivery={delivery}
          />

          <BookIdQRCode bookId={delivery.bookId} />

          {delivery.barcodeCodeBySpotCode && (
            <BarcodeForSpecificCorpUser
              code={delivery.barcodeCodeBySpotCode}
              title={delivery.barcodeTitleBySpotCode || null}
            />
          )}

          <PrintBreak />
        </WaybillContainer>

        <RestProductComponets
          numOfLinesRestProductNames={delivery.numOfLinesRestProductNames}
          restProductNames={delivery.restProductNames}
        />
      </>
    );
  },
);

export default function Print100150V3BlankComponent({
  deliveries = [],
  onLoaded = _noop,
}) {
  const loadedCount = useRef(0);

  const handleAfterRendered = useCallback(() => {
    loadedCount.current++;
    if (loadedCount.current === deliveries.length) {
      onLoaded();
    }
  }, [loadedCount.current]);

  return (
    <PrintContainer style={PrintContainerStyle}>
      {deliveries.map((d, index) => {
        return (
          <WaybillWrap
            key={index}
            delivery={d}
            count={index + 1}
            totalCount={deliveries.length}
            handleAfterRendered={handleAfterRendered}
          />
        );
      })}
    </PrintContainer>
  );
}
